import { useEffect, useState, useRef } from "react";
import API from "../../services/api";
import Navbar from "../../components/Navbar";
import AddUserModal from "./AddUserModal";
import EditUserModal from "./EditUserModal";
import FreeMembershipModal from "./FreeMembershipModal";
import UserDetailsModal from "./UserDetailsModal";

interface User {
    _id: string;
    full_name: string;
    email: string;
    phone_prefix: string;
    phone_number: string;
    address: string;
    has_active_membership: boolean;
    wallet_balance: number;
    createdAt: string;
    updatedAt: string;
}

interface UserResponse {
    users: User[];
    pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
    };
}

export default function AdminUsers() {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [showFreeMembershipModal, setShowFreeMembershipModal] = useState<boolean>(false);
    const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    const [searchTerm, setSearchTerm] = useState<string>("");
    const [membershipFilter, setMembershipFilter] = useState<string>("");
    const [page, setPage] = useState<number>(1);
    const [limit] = useState<number>(20);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalItems, setTotalItems] = useState<number>(0);

    const abortControllerRef = useRef<AbortController | null>(null);
    const requestIdRef = useRef<number>(0);

    useEffect(() => {
        const styleId = 'admin-users-styles';
        if (!document.getElementById(styleId)) {
            const styleSheet = document.createElement("style");
            styleSheet.id = styleId;
            styleSheet.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
            document.head.appendChild(styleSheet);
        }

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const fetchUsers = async () => {
        const currentRequestId = ++requestIdRef.current;

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        setLoading(true);
        setError(null);

        try {
            let url = `/users?page=${page}&limit=${limit}`;

            if (searchTerm) {
                url += `&search=${encodeURIComponent(searchTerm)}`;
            }

            if (membershipFilter) {
                url += `&has_membership=${membershipFilter}`;
            }

            const res = await API.get(url, {
                signal: abortControllerRef.current.signal
            });

            if (currentRequestId !== requestIdRef.current) {
                return;
            }

            if (res.data && res.data.success) {
                const data: UserResponse = res.data.data;
                setUsers(data.users);
                setTotalPages(data.pagination.totalPages);
                setTotalItems(data.pagination.totalItems);
                setError(null);
            } else {
                setError("Failed to fetch users");
            }
        } catch (error: any) {
            if (currentRequestId !== requestIdRef.current) {
                return;
            }
            if (error.name === 'AbortError') {
                return;
            }
            console.error("Failed to fetch users", error);
            setError(error?.response?.data?.message || "Failed to fetch users");
        } finally {
            if (currentRequestId === requestIdRef.current) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchUsers();
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [page, membershipFilter]);

    const handleSearch = () => {
        setPage(1);
        fetchUsers();
    };

    const handleClearFilters = () => {
        setSearchTerm("");
        setMembershipFilter("");
        setPage(1);

        setTimeout(() => {
            fetchUsers();
        }, 0);
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;

        try {
            const res = await API.delete(`/user/${id}`);
            if (res.data && res.data.success) {
                fetchUsers();
            }
        } catch (error: any) {
            console.error("Failed to delete user", error);
            alert(error?.response?.data?.message || "Failed to delete user");
        }
    };

    const handleEdit = (user: User) => {
        setSelectedUser(user);
        setShowEditModal(true);
    };

    const handleViewDetails = (user: User) => {
        setSelectedUser(user);
        setShowDetailsModal(true);
    };

    const handleFreeMembership = (user: User) => {
        if (!user.has_active_membership) {
            alert("This user doesn't have an active membership! Free coupon can only be applied to existing members.");
            return;
        }
        setSelectedUser(user);
        setShowFreeMembershipModal(true);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getMembershipBadgeStyle = (hasMembership: boolean): React.CSSProperties => ({
        display: 'inline-block',
        padding: '4px 14px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        backgroundColor: hasMembership ? '#2196f3' : '#9e9e9e',
        color: '#fff',
    });

    const renderPageNumbers = () => {
        if (totalPages === 0) return null;

        const pageNumbers: number[] = [];
        const currentPage = page;

        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else if (currentPage <= 3) {
            for (let i = 1; i <= 5; i++) {
                pageNumbers.push(i);
            }
        } else if (currentPage >= totalPages - 2) {
            for (let i = totalPages - 4; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            for (let i = currentPage - 2; i <= currentPage + 2; i++) {
                pageNumbers.push(i);
            }
        }

        return pageNumbers.map((pageNum) => (
            <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                style={{
                    ...styles.pageNumberButton,
                    ...(pageNum === currentPage ? styles.pageNumberActive : {})
                }}
            >
                {pageNum}
            </button>
        ));
    };

    return (
        <>
            <Navbar />
            <div style={styles.container}>
                <div style={styles.header}>
                    <div>
                        <h1 style={styles.title}>User Management</h1>
                        <p style={styles.subtitle}>Manage all users, their memberships, and wallet balances</p>
                    </div>
                    {/* <button
                        style={styles.createButton}
                        onClick={() => setShowAddModal(true)}
                    >
                        + Add User
                    </button> */}
                </div>

                <div style={styles.filtersContainer}>
                    <div style={styles.filterRow}>
                        <div style={styles.searchGroup}>
                            <input
                                type="text"
                                placeholder="Search by name, email, or phone..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                style={styles.searchInput}
                            />
                            <button onClick={handleSearch} style={styles.searchButton}>
                                🔍 Search
                            </button>
                            <button onClick={handleClearFilters} style={styles.clearButton}>
                                ✕ Clear Filters
                            </button>
                        </div>

                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Membership:</label>
                            <select
                                value={membershipFilter}
                                onChange={(e) => setMembershipFilter(e.target.value)}
                                style={styles.filterSelect}
                            >
                                <option value="">All</option>
                                <option value="true">Has Membership</option>
                                <option value="false">No Membership</option>
                            </select>
                        </div>
                    </div>

                    <div style={styles.filterRow}>
                        {totalItems > 0 && (
                            <span style={styles.totalItems}>
                                Total: {totalItems} user{totalItems > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                </div>

                {loading && (
                    <div style={styles.loading}>
                        <div style={styles.spinner}></div>
                        <p>Loading users...</p>
                    </div>
                )}

                {error && (
                    <div style={styles.error}>
                        <p>{error}</p>
                        <button onClick={fetchUsers} style={styles.retryButton}>
                            Retry
                        </button>
                    </div>
                )}

                {!loading && !error && (
                    <>
                        {users.length === 0 ? (
                            <div style={styles.emptyState}>
                                <div style={styles.emptyIcon}>👤</div>
                                <h3>No users found</h3>
                                <p style={styles.emptyText}>Add your first user to get started</p>
                            </div>
                        ) : (
                            <>
                                <div style={styles.tableContainer}>
                                    <table style={styles.table}>
                                        <thead>
                                            <tr style={styles.tableHeader}>
                                                <th style={styles.tableHeaderCell}>User</th>
                                                <th style={styles.tableHeaderCell}>Contact</th>
                                                <th style={styles.tableHeaderCell}>Membership</th>
                                                <th style={styles.tableHeaderCell}>Wallet</th>
                                                <th style={styles.tableHeaderCell}>Created At</th>
                                                <th style={styles.tableHeaderCell}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.map((user) => (
                                                <tr key={user._id} style={styles.tableRow}>
                                                    <td style={styles.tableCell}>
                                                        <div style={styles.userInfo}>
                                                            <div style={styles.userAvatarPlaceholder}>
                                                                {user.full_name.charAt(0).toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <div style={styles.userName}>{user.full_name}</div>
                                                                <div style={styles.userEmail}>{user.email || 'No email'}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={styles.tableCell}>
                                                        <div style={styles.contactInfo}>
                                                            <div>{user.phone_prefix} {user.phone_number}</div>
                                                            <div style={styles.userAddress}>
                                                                {user.address.length > 40 ? user.address.slice(0, 40) + '...' : user.address}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={styles.tableCell}>
                                                        <span style={getMembershipBadgeStyle(user.has_active_membership)}>
                                                            {user.has_active_membership ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td style={styles.tableCell}>
                                                        <span style={styles.walletBalance}>
                                                            ₹{user.wallet_balance.toFixed(2)}
                                                        </span>
                                                    </td>
                                                    <td style={styles.tableCell}>
                                                        <div style={styles.createdAt}>
                                                            {formatDate(user.createdAt)}
                                                        </div>
                                                    </td>
                                                    <td style={styles.tableCell}>
                                                        <div style={styles.actionButtons}>
                                                            <button
                                                                onClick={() => handleViewDetails(user)}
                                                                style={{ ...styles.actionButton, ...styles.viewButton }}
                                                                title="View Details"
                                                            >
                                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                                    <circle cx="12" cy="12" r="3" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => handleFreeMembership(user)}
                                                                style={{
                                                                    ...styles.actionButton,
                                                                    ...styles.freeMembershipButton,
                                                                    ...(!user.has_active_membership ? styles.actionButtonDisabled : {})
                                                                }}
                                                                title={user.has_active_membership ? "Already has membership" : "Free Membership"}
                                                                disabled={!user.has_active_membership}
                                                            >
                                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path d="M2 20L4 6L8 10L12 4L16 10L20 6L22 20Z" />
                                                                    <path d="M4 20H20" />
                                                                    <circle cx="12" cy="14" r="2" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(user)}
                                                                style={styles.actionButton}
                                                                title="Edit"
                                                            >
                                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(user._id)}
                                                                style={{ ...styles.actionButton, ...styles.deleteButton }}
                                                                title="Delete"
                                                            >
                                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <polyline points="3 6 5 6 21 6" />
                                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {totalPages > 1 && (
                                    <div style={styles.pagination}>
                                        <button
                                            onClick={() => handlePageChange(page - 1)}
                                            disabled={page === 1}
                                            style={{
                                                ...styles.pageButton,
                                                ...(page === 1 ? styles.pageButtonDisabled : {})
                                            }}
                                        >
                                            ← Previous
                                        </button>

                                        <div style={styles.pageNumbers}>
                                            {renderPageNumbers()}
                                        </div>

                                        <button
                                            onClick={() => handlePageChange(page + 1)}
                                            disabled={page === totalPages}
                                            style={{
                                                ...styles.pageButton,
                                                ...(page === totalPages ? styles.pageButtonDisabled : {})
                                            }}
                                        >
                                            Next →
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>

            {showAddModal && (
                <AddUserModal
                    onClose={() => setShowAddModal(false)}
                    onSuccess={() => {
                        setShowAddModal(false);
                        fetchUsers();
                    }}
                />
            )}

            {showEditModal && selectedUser && (
                <EditUserModal
                    user={selectedUser}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedUser(null);
                    }}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setSelectedUser(null);
                        fetchUsers();
                    }}
                />
            )}

            {showDetailsModal && selectedUser && (
                <UserDetailsModal
                    user={selectedUser}
                    onClose={() => {
                        setShowDetailsModal(false);
                        setSelectedUser(null);
                    }}
                />
            )}

            {showFreeMembershipModal && selectedUser && (
                <FreeMembershipModal
                    user={selectedUser}
                    onClose={() => {
                        setShowFreeMembershipModal(false);
                        setSelectedUser(null);
                    }}
                    onSuccess={() => {
                        setShowFreeMembershipModal(false);
                        setSelectedUser(null);
                        fetchUsers();
                    }}
                />
            )}
        </>
    );
}

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        padding: "24px",
        maxWidth: "1400px",
        margin: "0 auto",
        backgroundColor: "#f5f7fa",
        minHeight: "100vh",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "24px",
    },
    title: {
        fontSize: "28px",
        fontWeight: "700",
        color: "#1a2332",
        margin: 0,
    },
    subtitle: {
        fontSize: "14px",
        color: "#666",
        marginTop: "4px",
    },
    createButton: {
        padding: "10px 24px",
        backgroundColor: "#6c5ce7",
        color: "#fff",
        border: "none",
        borderRadius: "8px",
        fontSize: "14px",
        fontWeight: "600",
        cursor: "pointer",
        transition: "background-color 0.2s",
    },
    filtersContainer: {
        backgroundColor: "#fff",
        padding: "16px 20px",
        borderRadius: "10px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        marginBottom: "24px",
    },
    filterRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "16px",
        flexWrap: "wrap",
    },
    searchGroup: {
        display: "flex",
        gap: "8px",
        flexWrap: "wrap",
        flex: 1,
    },
    searchInput: {
        padding: "8px 14px",
        borderRadius: "6px",
        border: "1px solid #ddd",
        fontSize: "14px",
        outline: "none",
        minWidth: "200px",
        flex: 1,
    },
    searchButton: {
        padding: "8px 16px",
        backgroundColor: "#6c5ce7",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
    },
    clearButton: {
        padding: "8px 16px",
        backgroundColor: "#f5f5f5",
        color: "#666",
        border: "1px solid #ddd",
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "14px",
    },
    filterGroup: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    filterLabel: {
        fontSize: "14px",
        fontWeight: "500",
        color: "#555",
    },
    filterSelect: {
        padding: "8px 12px",
        borderRadius: "6px",
        border: "1px solid #ddd",
        fontSize: "14px",
        outline: "none",
        backgroundColor: "#fff",
    },
    totalItems: {
        fontSize: "14px",
        fontWeight: "500",
        color: "#666",
        marginLeft: "auto",
    },
    loading: {
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 20px",
        color: "#666",
    },
    spinner: {
        width: "40px",
        height: "40px",
        border: "4px solid #f3f3f3",
        borderTop: "4px solid #6c5ce7",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        marginBottom: "16px",
    },
    error: {
        backgroundColor: "#fff3f3",
        border: "1px solid #f44336",
        borderRadius: "8px",
        padding: "16px 20px",
        marginBottom: "20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        color: "#d32f2f",
    },
    retryButton: {
        padding: "8px 20px",
        backgroundColor: "#f44336",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "600",
    },
    emptyState: {
        textAlign: "center" as const,
        padding: "60px 20px",
        backgroundColor: "#fff",
        borderRadius: "10px",
        border: "1px dashed #ddd",
    },
    emptyIcon: {
        fontSize: "48px",
        marginBottom: "16px",
    },
    emptyText: {
        color: "#888",
        fontSize: "14px",
    },
    tableContainer: {
        backgroundColor: "#fff",
        borderRadius: "10px",
        overflow: "auto",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        marginBottom: "20px",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse" as const,
        minWidth: "900px",
    },
    tableHeader: {
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #e8ecf1",
    },
    tableHeaderCell: {
        padding: "12px 16px",
        textAlign: "left" as const,
        fontSize: "13px",
        fontWeight: "600",
        color: "#555",
        textTransform: "uppercase" as const,
        letterSpacing: "0.5px",
    },
    tableRow: {
        borderBottom: "1px solid #e8ecf1",
        transition: "background-color 0.2s",
        cursor: "pointer",
    },
    tableCell: {
        padding: "12px 16px",
        fontSize: "14px",
        color: "#333",
        verticalAlign: "middle" as const,
    },
    userInfo: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
    },
    userAvatarPlaceholder: {
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        backgroundColor: "#6c5ce7",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "18px",
        fontWeight: "600",
    },
    userName: {
        fontWeight: "600",
        color: "#1a2332",
    },
    userEmail: {
        fontSize: "12px",
        color: "#888",
    },
    contactInfo: {
        fontSize: "13px",
    },
    userAddress: {
        fontSize: "12px",
        color: "#888",
        marginTop: "2px",
    },
    walletBalance: {
        fontWeight: "600",
        color: "#4caf50",
        fontSize: "15px",
    },
    actionButtons: {
        display: "flex",
        gap: "6px",
        flexWrap: "wrap" as const,
    },
    actionButton: {
        width: "34px",
        height: "34px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f0f2f5",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        transition: "all 0.2s",
        color: "#555",
        padding: 0,
    },
    actionButtonDisabled: {
        opacity: 0.5,
        cursor: "not-allowed",
        pointerEvents: "none",
    },
    viewButton: {
        backgroundColor: "#e3f2fd",
        color: "#1976d2",
    },
    freeMembershipButton: {
        backgroundColor: "#fff3e0",
        color: "#e65100",
    },
    deleteButton: {
        color: "#f44336",
    },
    pagination: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",
        padding: "16px 0",
        flexWrap: "wrap",
    },
    pageButton: {
        padding: "8px 16px",
        backgroundColor: "#fff",
        border: "1px solid #ddd",
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
        color: "#333",
        transition: "all 0.2s",
    },
    pageButtonDisabled: {
        opacity: 0.5,
        cursor: "not-allowed",
    },
    pageNumbers: {
        display: "flex",
        gap: "4px",
    },
    pageNumberButton: {
        width: "36px",
        height: "36px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#fff",
        border: "1px solid #ddd",
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
        color: "#333",
        transition: "all 0.2s",
    },
    pageNumberActive: {
        backgroundColor: "#6c5ce7",
        borderColor: "#6c5ce7",
        color: "#fff",
    },
};